import React from 'react';
import { Progress } from 'reactstrap';
import moment from 'moment';
import { Link } from '@s20/components/common/Link';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    EditLinkCellRenderer,
    VariableLinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
    booleanCellRenderer,
    DateRangeReadOnlyFloatingFilterComponent,
} from '../../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../../common/dataGrid/DataGridSelectFloatingFilter';
import CommonContext, { ApiRoutes, AppNavPaths } from '../../Common';
import DataGridToolbar from '../../common/dataGrid/DataGridToolbar';
import { PageWrap } from '../../common/forms/FormElements';
import CallLogForm from './CallLogForm';

class CallLogIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.state = {
            loading: true,
        };

        this.callLogFormRef = React.createRef();
    }

    componentDidMount() {
        return this.populateState();
    }

    onCallLogFormSaved = () => {
        const { gridOptions } = this.state;
        gridOptions.refresh();
    };

    onEditCallLog = (id) => {
        this.callLogFormRef.current.open(id);
    };

    onAddCallLog = () => {
        this.callLogFormRef.current.openNew();
    };

    async populateState() {
        const [
            interactionTypes,
            directions,
            dispositions,
            dispatchLocations,
        ] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.callLogInteractionTypes()),
            httpClient.js(ApiRoutes.callLog.directions()),
            httpClient.js(ApiRoutes.callLog.dispositions()),
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
        ]);

        const interactionTypeFilterParams = {
            suppressFilterButton: true,
            labelText: 'Filter by Interaction Type',
            options: interactionTypes,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        const directionFilterParams = {
            suppressFilterButton: true,
            labelText: 'Filter by Direction',
            options: directions,
            optionsLabel: 'description',
            optionsValue: 'id',
        };

        const dispositionFilterParams = {
            suppressFilterButton: true,
            labelText: 'Filter by Disposition',
            options: dispositions,
            optionsLabel: 'description',
            optionsValue: 'id',
        };

        const dispatchFilterParams = {
            suppressFilterButton: true,
            labelText: 'Filter by Dispatch Office',
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        const followUpFilterParams = {
            suppressFilterButton: true,
            labelText: 'Filter by Follow Up',
            options: [
                { name: 'Yes', id: 'true' },
                { name: 'No', id: 'false' },
            ],
            optionsLabel: 'name',
            optionsValue: 'id',
        };

        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: EditLinkCellRenderer,
            dateTimeLinkRenderer: VariableLinkCellRenderer,
        };

        gridOptions.columnDefs = [
            {
                colId: 'CallDateTime',
                sortable: true,
                headerName: 'Date/Time',
                field: 'callDateTime',
                sort: 'desc',
                valueFormatter: (params) => (params.value ? moment(params.value).format('M/D/YYYY h:mm A') : ''),
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
                floatingFilter: true,
                floatingFilterComponent: DateRangeReadOnlyFloatingFilterComponent,
                minWidth: 200,
                cellRenderer: 'dateTimeLinkRenderer',
                cellRendererParams: {
                    clicked: this.onEditCallLog,
                    idField: 'id',
                    isLink: true,
                    title: 'View this Call Log',
                    valueGetter: (props) => (props.data.callDateTime
                        ? moment(props.data.callDateTime).format('M/D/YYYY h:mm A') : ''),
                },
            },
            {
                colId: 'CallLogInteractionTypeId',
                sortable: true,
                headerName: 'Type',
                field: 'callLogInteractionTypeDescription',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: interactionTypeFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: interactionTypeFilterParams,
            },
            {
                colId: 'CallDirectionId',
                sortable: true,
                headerName: 'Direction',
                field: 'callDirection.description',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: directionFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: directionFilterParams,
            },
            {
                colId: 'CallDispositionId',
                sortable: true,
                headerName: 'Disposition',
                field: 'callDisposition.description',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: dispositionFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispositionFilterParams,
            },
            {
                colId: 'DispatchOfficeId',
                sortable: true,
                headerName: 'Dispatch Office',
                field: 'dispatchOfficeName',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'CustomerCompanyName',
                sortable: true,
                headerName: 'Customer',
                field: 'customerCompanyName',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilter: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: (params) => {
                    if (!params?.data) return null;

                    const { customerCompanyId, customerCompanyName } = params.data;
                    if (!customerCompanyId || !customerCompanyName) return customerCompanyName ?? '';

                    return (
                        <Link
                            className="site-link"
                            to={`${AppNavPaths.Organization}/${customerCompanyId}`}
                        >
                            {customerCompanyName}
                        </Link>
                    );
                },
            },
            {
                colId: 'ContractNumber',
                sortable: true,
                headerName: 'Contract #',
                field: 'contractNumber',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilter: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: (params) => {
                    if (!params?.data) return null;

                    const { contractId, contractNumber } = params.data;
                    if (!contractId || !contractNumber) return contractNumber ?? '';

                    return (
                        <Link
                            className="site-link"
                            to={`${AppNavPaths.Contract}/${contractId}`}
                        >
                            {contractNumber}
                        </Link>
                    );
                },
            },
            {
                colId: 'JobNumber',
                sortable: true,
                headerName: 'Job #',
                field: 'jobNumber',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilter: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: (params) => {
                    if (!params?.data) return null;

                    const { jobId, jobNumber } = params.data;
                    if (!jobId || !jobNumber) return jobNumber ?? '';

                    return (
                        <Link
                            className="site-link"
                            to={`${AppNavPaths.Job}/${jobId}`}
                        >
                            {jobNumber}
                        </Link>
                    );
                },
            },
            {
                colId: 'ContactFullName',
                sortable: true,
                headerName: 'Contact',
                field: 'contactFullName',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilter: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: 'nameRenderer',
                cellRendererParams: {
                    clicked: this.onEditCallLog,
                    idField: 'id',
                    nameField: 'contactFullName',
                    title: 'View this Call Log',
                    entity: 'callLog',
                },
            },
            {
                colId: 'ContactPhoneNumber',
                sortable: true,
                headerName: 'Phone',
                field: 'contactPhoneNumber',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilter: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'FollowUpRequested',
                sortable: true,
                headerName: 'Follow Up',
                field: 'followUpRequested',
                cellRenderer: booleanCellRenderer,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: followUpFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: followUpFilterParams,
                maxWidth: 120,
            },
            {
                colId: 'CreatedByName',
                sortable: true,
                headerName: 'Created By',
                field: 'createdByName',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilter: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
        ];

        const dataSource = createDataSource(
            ApiRoutes.callLog.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            directions,
            dispositions,
        });
    }

    render() {
        if (this.state.loading) return <Progress />;

        const { rowData, gridOptions } = this.state;

        return (
            <>
                <PageWrap>
                    <DataGridToolbar
                        entity="callLog"
                        gridApi={this.state.gridApi}
                        dataSource={this.state.dataSource}
                        onAdd={this.onAddCallLog}
                        addLabel="New Call Log"
                        hideExcelButton
                        gridOptions={gridOptions}
                    />
                    <DataGrid
                        domLayout="normal"
                        rowData={rowData}
                        gridOptions={gridOptions}
                        gridStatus={this.state.gridStatus}
                    />
                </PageWrap>
                <CallLogForm
                    ref={this.callLogFormRef}
                    onSaveCallback={this.onCallLogFormSaved}
                />
            </>
        );
    }
}

export default CallLogIndex;
